import { useState } from "react";
import { twMerge } from "tailwind-merge";

import EditEmailModal from "components/modals/creative-modal/components/edit-email-modal";
import EditTouchpointModal from "components/modals/creative-modal/components/edit-touchpoint-modal";
import { ConfirmationDialog } from "ui/confirmation-dialog";

import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";

import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";

import { getActionTouchpointTypeLabel } from "utilities";

import { TouchpointType } from "types/touchpoint";

import ContentDetailsCard from "containers/admin/clients/touchpoint/tab-components/details-tab/components/content-details-card";
import IterableTemplateIdsCard from "containers/admin/clients/touchpoint/tab-components/details-tab/components/iterable-template-ids-card";
import EmailVersionContentSettingsCard from "containers/admin/clients/touchpoint/tab-components/details-tab/components/email-version-content-settings-card";
import LPVersionContentSettingsCard from "containers/admin/clients/touchpoint/tab-components/details-tab/components/lp-version-content-settings-card";
import TouchpointLinksCard from "containers/admin/clients/touchpoint/tab-components/details-tab/components/touchpoint-links-card";

interface DetailsTabProps {
  selectedVersion?: TouchpointVersionAttributes;
}

const responsiveColumn = "flex-1 min-w-full md:min-w-[calc(50%-0.75rem)]";

const DetailsTab = ({ selectedVersion }: DetailsTabProps) => {
  const currentTouchpoint = useCurrentTouchpoint();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const isEmail = currentTouchpoint.type === TouchpointType.EMAIL;
  const showLPContentSettings =
    !isEmail && currentTouchpoint.type !== TouchpointType.OTHER;

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };

  const handleConfirmClose = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmExit = () => {
    setShowConfirmationModal(false);
    setIsEditModalOpen(false);
  };

  const editType = getActionTouchpointTypeLabel(currentTouchpoint.type).toLowerCase();

  return (
    <>
      <div className="flex flex-wrap gap-y-4 gap-x-6">
        <div className={responsiveColumn}>
          <ContentDetailsCard
            touchpoint={currentTouchpoint}
            onEditClick={() => setIsEditModalOpen(true)}
          />
        </div>

        {(isEmail || showLPContentSettings) && (
          <div className={responsiveColumn}>
            {isEmail ? (
              <IterableTemplateIdsCard touchpoint={currentTouchpoint} />
            ) : (
              <LPVersionContentSettingsCard
                touchpoint={currentTouchpoint}
                selectedVersion={selectedVersion}
              />
            )}
          </div>
        )}

        {isEmail ? (
          <div className={responsiveColumn}>
            <EmailVersionContentSettingsCard
              touchpoint={currentTouchpoint}
              selectedVersion={selectedVersion}
            />
          </div>
        ) : showLPContentSettings ? (
          <div className={twMerge(responsiveColumn, "hidden md:block")} />
        ) : null}

        <div className={responsiveColumn}>
          <TouchpointLinksCard touchpoint={currentTouchpoint} selectedVersion={selectedVersion} />
        </div>
      </div>
      {isEmail ? (
        <EditEmailModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          onConfirmClose={handleConfirmClose}
          touchpoint={currentTouchpoint}
        />
      ) : (
        <EditTouchpointModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          onConfirmClose={handleConfirmClose}
          touchpoint={currentTouchpoint}
        />
      )}
      {showConfirmationModal && (
        <ConfirmationDialog
          open
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirmExit}
          intent="warning"
          closable={false}
          headline="Discard changes"
          message={`This action will close 'Edit ${editType}' modal and permanently delete your additions.`}
          cancelButtonText="Continue editing"
          confirmButtonText="Leave setup"
        />
      )}
    </>
  );
};

export default DetailsTab;
